import type { TunaThunkDispatch } from '@tunasong/redux'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { prefetchRecent, useRecent } from './recent.hook.js'

const DEFAULT_MAX_COUNT = 30
const DEFAULT_MINE_LIMIT = 6

export interface UseRecentSplitProps {
  /** Forwarded to `useRecent` as the server-side fetch ceiling. */
  maxCount?: number
  /** Cap on the `mine` slice. The Continue-working section consumes at most this many. */
  mineLimit?: number
}

export interface RecentSplitResult {
  mine: Persisted<Entity>[]
  theirs: Persisted<Entity>[]
  /** The feed has resolved. False while its query is skipped or delayed, when `isLoading` is false too (GH #2914) */
  isSuccess: boolean
  isLoading: boolean
  /** The feed's query failed; `retry` runs it again */
  isError: boolean
  retry(): void
}

/** Start `useRecentSplit`'s feed before it mounts, e.g. on Home's first screen (GH #2903) */
export const prefetchRecentSplit = (dispatch: TunaThunkDispatch, props: Pick<UseRecentSplitProps, 'maxCount'> = {}) =>
  prefetchRecent(dispatch, { maxCount: props.maxCount ?? DEFAULT_MAX_COUNT })

/**
 * Split the existing server-side recent feed into `mine` (entities I last-edited)
 * and `theirs` (everything else, including legacy entries without `lastEditedBy`).
 * Pure derivation — no new server query. v1 trades tail correctness for simplicity;
 * pinning covers long-tail items.
 */
export const useRecentSplit = (props: UseRecentSplitProps = {}): RecentSplitResult => {
  const { maxCount = DEFAULT_MAX_COUNT, mineLimit = DEFAULT_MINE_LIMIT } = props
  const me = useCurrentUser()
  const { searchResults, isLoading, isSuccess, isError, retry } = useRecent({ maxCount })
  const mine = searchResults.filter(e => e.lastEditedBy === me.userId).slice(0, mineLimit)
  const theirs = searchResults.filter(e => e.lastEditedBy !== me.userId)
  return { mine, theirs, isSuccess, isLoading, isError, retry }
}
